import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import SEO from './components/SEO';

import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LogoSlider from './components/LogoSlider';
import Demo from './components/Demo';
import Partners from './components/Partners';
import Features from './components/Features';
import CollaborativeTools from './components/CollaborativeTools';
import CustomBranding from './components/CustomBranding';
import Gamification from './components/Gamification';
import FeedbackSection from './components/FeedbackSection';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import PricingStrategies from './components/PricingStrategies';
import CommunicationHub from './components/CommunicationHub';
import Calculator from './components/Calculator';
import Formats from './components/Formats';
import FAQ from './components/FAQ';
import Pricing from './components/Pricing';
import Footer from './components/Footer';
import TermOfUseForAttendees from './components/TermOfUseForAttendees';
import TermOfUseForOrganizers from './components/TermOfUseForOrganizers';
import PrivacyPolicy from './components/PrivacyPolicy';
import SolutionsPage from './pages/SolutionsPage';
import ScrollToTop from './components/ScrollToTop';

function HashScroller() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 120);
      }
    }
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function SolutionsRedirect() {
  const { lang } = useParams();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'ecosystem';
  const targetLang = lang || 'en';
  return <Navigate to={`/${targetLang}?page=solutions&tab=${tab}`} replace />;
}

function TermsAttendeesRedirect() {
  const { lang } = useParams();
  const targetLang = lang || 'en';
  return <Navigate to={`/${targetLang}?page=terms-attendees`} replace />;
}

function TermsOrganizersRedirect() {
  const { lang } = useParams();
  const targetLang = lang || 'en';
  return <Navigate to={`/${targetLang}?page=terms-organizers`} replace />;
}

function PrivacyRedirect() {
  const { lang } = useParams();
  const targetLang = lang || 'en';
  return <Navigate to={`/${targetLang}?page=privacy-policy`} replace />;
}

function MainContent() {
  return (
    <div className="min-h-screen font-sans selection:bg-indigo-100 selection:text-indigo-900">
      <Navbar />
      <main>
        <Hero />
        <LogoSlider />
        <Demo />
        <Partners />
        <Features />
        <CollaborativeTools />
        <CustomBranding />
        {/* <Gamification /> */}
        <FeedbackSection />
        <AnalyticsDashboard />
        <PricingStrategies />
        <CommunicationHub />
        <Calculator />
        <Formats />
        <Pricing />
        <FAQ />
      </main>
      <Footer />
    </div>
  );
}

function PageContent() {
  const [searchParams] = useSearchParams();
  const pageParam = searchParams.get('page') || searchParams.get('pages');

  if (pageParam === 'terms-attendees') {
    return (
      <>
        <SEO titleOverride="Terms of Use for Attendees | BookMeIn.Events" descriptionOverride="Attendee terms and conditions for BookMeIn.Events. Please read carefully before purchasing tickets." />
        <TermOfUseForAttendees />
      </>
    );
  }

  if (pageParam === 'terms-organizers') {
    return (
      <>
        <SEO titleOverride="Terms of Use for Organizers | BookMeIn.Events" descriptionOverride="Organizer terms and conditions for BookMeIn.Events. Please read carefully before creating events and selling tickets." />
        <TermOfUseForOrganizers />
      </>
    );
  }

  if (pageParam === 'privacy-policy' || pageParam === 'privacy') {
    return (
      <>
        <SEO 
          titleOverride="Privacy Policy & Data Usage | BookMeIn.Events" 
          descriptionOverride="How BookMeIn.Events collects, processes, and protects your personal data — covering controllers, legal bases, hosting, retention, your rights, cookies, and security." 
        />
        <PrivacyPolicy />
      </>
    );
  }

  if (pageParam === 'solutions') {
    return <SolutionsPage />;
  }

  return (
    <>
      <SEO />
      <MainContent />
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <HashScroller />
      <Routes>
        {/* Solutions Legacy Redirects */}
        <Route path="/solutions" element={<SolutionsRedirect />} />
        <Route path="/:lang/solutions" element={<SolutionsRedirect />} />

        {/* Standalone Terms & Privacy Canonical Redirects to ?page=... */}
        <Route path="/terms-attendees" element={<Navigate to="/en?page=terms-attendees" replace />} />
        <Route path="/terms-organizers" element={<Navigate to="/en?page=terms-organizers" replace />} />
        <Route path="/privacy-policy" element={<Navigate to="/en?page=privacy-policy" replace />} />
        <Route path="/privacy" element={<Navigate to="/en?page=privacy-policy" replace />} />

        {/* Localized Terms & Privacy Canonical Redirects to /:lang?page=... */}
        <Route path="/:lang/terms-attendees" element={<TermsAttendeesRedirect />} />
        <Route path="/:lang/terms-organizers" element={<TermsOrganizersRedirect />} />
        <Route path="/:lang/privacy-policy" element={<PrivacyRedirect />} />
        <Route path="/:lang/privacy" element={<PrivacyRedirect />} />

        {/* Localized Home & Query Param Page Route */}
        <Route path="/:lang" element={<PageContent />} />

        {/* Root Route */}
        <Route path="/" element={<PageContent />} />

        {/* Catch-all Fallback */}
        <Route path="*" element={<Navigate to="/en" replace />} />
      </Routes>
      <ScrollToTop />
    </LanguageProvider>
  );
}

export default App;